import { useCallback, useMemo } from "react";
import { hasNumberRangeValue, type ColumnVisibility, type FilterConfig, type FilterValues, type NumberRangeValue } from "../datagrid-configs/types";
import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import CreatableSelect from "react-select/creatable";
import { AdminMultiSelectComponent, AdminSelectComponent } from "components/select";
import { adminSelectBaseProps, adminSelectStyles } from "components/select/adminSelectStyles";
import type { SelectValues } from "ui-kit/src/MultipleSelect/types";
import { DateTimeRangeCalendar } from "ui-kit/src/DateTimeRangeCalendar/DateTimeRangeCalendar";
import DateRangeCalendarExtended from "ui-kit/src/DataRangeCalendarExtended/DateRangeCalendar";
import { NumberRangeFilter } from "./NumberRangeFilter";
import { useI18n } from "features/translation/context";
import "./DatagridFilters.css";

interface DatagridFiltersProps {
	/** Filter configurations */
	filters: FilterConfig[];
	/** Current filter values */
	filterValues: FilterValues;
	/** Callback when filter values change (applies immediately) */
	onFilterChange: (values: FilterValues | ((previousValues: FilterValues) => FilterValues)) => void;
	/** Callback when filters are cleared */
	onClear: () => void;
	/** Whether filters are collapsed */
	isCollapsed: boolean;
	/** Callback to toggle collapse state */
	onToggleCollapse: () => void;
	/** Dynamic options for select filters (keyed by filter id) */
	dynamicOptions?: Record<string, Array<{ value: string | number; label: string }>>;
	/** When set, only show filters for columns that are visible (hide filters for hidden columns) */
	columnVisibility?: ColumnVisibility;
}

type FilterOption = { value: string | number; label: string };

/**
 * DatagridFilters - Horizontal filter bar for the DataGrid.
 * Displays filters based on their type configuration.
 * Filters apply immediately when changed.
 */
export function DatagridFilters({
	filters,
	filterValues,
	onFilterChange,
	onClear,
	isCollapsed,
	onToggleCollapse,
	dynamicOptions = {},
	columnVisibility,
}: DatagridFiltersProps) {
	const { locale } = useI18n();

	// Only show filters for visible columns when columnVisibility is provided
	const visibleFilters = useMemo(() => {
		if (!columnVisibility) return filters;
		return filters.filter((f) => columnVisibility[f.id] !== false);
	}, [filters, columnVisibility]);

	// Merge static options (e.g. NULL manager sentinel) with dynamic API options.
	// Precompute per filter id so select/multiselect children receive stable `items`/`options`
	// references when `filters` and `dynamicOptions` are unchanged (avoids needless re-renders).
	const { mergedOptionsByFilterId, multiSelectItemsByFilterId } = useMemo(() => {
		const merged: Record<string, FilterOption[]> = {};
		const multiSelectItems: Record<string, Array<{ value: string; label: string }>> = {};

		for (const filter of filters) {
			const dynamic = dynamicOptions[filter.id];
			const staticOpts = filter.options ?? [];
			let opts: FilterOption[];
			if (dynamic !== undefined && dynamic.length > 0) {
				opts = [...staticOpts, ...dynamic];
			} else if (staticOpts.length > 0) {
				opts = staticOpts;
			} else {
				opts = dynamic ?? [];
			}
			merged[filter.id] = opts;

			if (filter.type === "multiselect") {
				multiSelectItems[filter.id] = opts.map((opt) => ({
					value: String(opt.value),
					label: opt.label,
				}));
			}
		}

		return { mergedOptionsByFilterId: merged, multiSelectItemsByFilterId: multiSelectItems };
	}, [filters, dynamicOptions]);

	// Handle individual filter value change
	const handleFilterChange = useCallback(
		(filterId: string, value: unknown) => {
			onFilterChange((previousValues) => ({
				...previousValues,
				[filterId]: value,
			}));
		},
		[onFilterChange],
	);

	// Treat a single filter value as active only when it has a meaningful value.
	// Cleared date/number ranges become { start: undefined, end: undefined } or
	// { min: undefined, max: undefined } and must not count as active.
	const isFilterValueActive = (v: unknown): boolean => {
		if (v === undefined || v === null) return false;
		if (Array.isArray(v)) return v.length > 0;
		if (typeof v === "object" && v !== null) {
			const o = v as Record<string, unknown>;
			if ("start" in o || "end" in o) return !!(o.start ?? o.end);
			if ("min" in o || "max" in o) return hasNumberRangeValue(v);
			return true;
		}
		return v !== "";
	};

	const visibleFilterIds = useMemo(() => new Set(visibleFilters.map((f) => f.id)), [visibleFilters]);
	const hasActiveFilters = useMemo(() => {
		return Object.entries(filterValues).some(([id, value]) => visibleFilterIds.has(id) && isFilterValueActive(value));
	}, [filterValues, visibleFilterIds]);

	// Get SelectValue from raw value for SelectComponent
	const getSelectValue = (rawValue: unknown, options?: Array<{ value: string | number; label: string }>) => {
		if (rawValue === undefined || rawValue === null) return null;
		const opt = options?.find((o) => String(o.value) === String(rawValue));
		return opt ? { value: opt.value, label: opt.label } : null;
	};

	// Get SelectValues (array) from raw value for MultiSelectComponent
	const getMultiSelectValue = (rawValue: unknown, options?: Array<{ value: string | number; label: string }>): SelectValues | null => {
		if (rawValue === undefined || rawValue === null) return null;
		if (!Array.isArray(rawValue)) return null;
		if (rawValue.length === 0) return null;

		const matchedValues = rawValue
			.map((val) => {
				const opt = options?.find((o) => String(o.value) === String(val));
				if (opt) {
					return { value: String(opt.value), label: opt.label };
				}

				return { value: String(val), label: String(val) };
			});

		return matchedValues.length > 0 ? matchedValues : null;
	};

	// Render filter input based on type
	const renderFilter = (filter: FilterConfig) => {
		const value = filterValues[filter.id];
		const placeholder = filter.placeholder ?? filter.label;

		switch (filter.type) {
			case "text":
				return (
					<TableTextFilter
						placeholder={placeholder}
						title={filter.title}
						number={filter.numeric === true}
						value={(value as string) || ""}
						disabled={filter.disabled === true}
						onChange={(val) => {
							if (filter.disabled === true) {
								return;
							}
							handleFilterChange(filter.id, val || undefined);
						}}
						onSubmit={(val) => {
							if (filter.disabled === true) {
								return;
							}
							handleFilterChange(filter.id, val || undefined);
						}}
						clear={() => handleFilterChange(filter.id, undefined)}
					/>
				);

			case "select": {
				const selectOptions = mergedOptionsByFilterId[filter.id] ?? [];
				return (
					<AdminSelectComponent
						placeholder={placeholder}
						isDisabled={filter.disabled === true}
						value={getSelectValue(value, selectOptions)}
						items={selectOptions}
						handleChange={(selected) => {
							if (filter.disabled === true) {
								return;
							}
							handleFilterChange(filter.id, selected?.value ?? undefined);
						}}
					/>
				);
			}

			case "boolean": {
				const boolOptions = mergedOptionsByFilterId[filter.id] ?? [];
				return (
					<AdminSelectComponent
						placeholder={placeholder}
						value={getSelectValue(value === true ? "true" : value === false ? "false" : undefined, boolOptions)}
						items={boolOptions}
						handleChange={(selected) => {
							const val = selected?.value;
							handleFilterChange(filter.id, val === "true" ? true : val === "false" ? false : undefined);
						}}
					/>
				);
			}

			case "daterange": {
				const rangeValue = value as { start?: string; end?: string } | undefined;
				// Use DateRangeCalendarExtended for date-only (daterange), DateTimeRangeCalendar for datetime (datetimerange)
				if (filter.filterType === "daterange") {
					return (
						<DateRangeCalendarExtended
							placeholder={placeholder}
							apiFormat
							onChange={(start, end, _isReset) => {
								handleFilterChange(filter.id, {
									start: start || undefined,
									end: end || undefined,
								});
							}}
							start={rangeValue?.start || ""}
							end={rangeValue?.end || ""}
							locale={locale}
						/>
					);
				}
				// Default to DateTimeRangeCalendar for datetimerange
				return (
					<DateTimeRangeCalendar
						placeholder={placeholder}
						apiFormat
						onChange={(start, end) => {
							handleFilterChange(filter.id, {
								start: start || undefined,
								end: end || undefined,
							});
						}}
						start={rangeValue?.start || ""}
						end={rangeValue?.end || ""}
						locale={locale}
					/>
				);
			}

			case "multiselect": {
				const multiOptions = mergedOptionsByFilterId[filter.id] ?? [];
				const multiItems = multiSelectItemsByFilterId[filter.id] ?? [];
				const multiValue = getMultiSelectValue(value, multiOptions);

				if (filter.creatable === true) {
					const creatableStyles = {
						...adminSelectStyles,
						menu: (provided: Record<string, unknown>) => ({
							...(typeof adminSelectStyles.menu === "function" ? adminSelectStyles.menu(provided, {} as never) : provided),
							...(filter.wideMenu === true
								? {
										minWidth: "max(100%, min(360px, calc(100vw - 32px)))",
									}
								: {}),
						}),
					};

					return (
						<CreatableSelect
							{...adminSelectBaseProps}
							styles={creatableStyles}
							menuPortalTarget={document.body}
							isMulti
							isClearable
							isDisabled={filter.disabled === true}
							placeholder={placeholder}
							options={multiItems}
							value={multiValue}
							onChange={(selected) => {
								if (selected == null || selected.length === 0) {
									handleFilterChange(filter.id, undefined);
									return;
								}

								handleFilterChange(
									filter.id,
									selected.map((item) => item.value),
								);
							}}
						/>
					);
				}

				return (
					<AdminMultiSelectComponent
						wideMenu={filter.wideMenu === true}
						isDisabled={filter.disabled === true}
						placeholder={placeholder}
						value={multiValue}
						items={multiItems}
						handleChange={(selected) => {
							if (selected == null || selected.length === 0) {
								handleFilterChange(filter.id, undefined);
								return;
							}

							handleFilterChange(
								filter.id,
								selected.map((item) => item.value),
							);
						}}
					/>
				);
			}

			case "numberrange": {
				const rangeValue = value as NumberRangeValue | undefined;
				return (
					<NumberRangeFilter
						placeholder={placeholder}
						value={rangeValue}
						onChange={(newValue) => {
							handleFilterChange(filter.id, newValue);
						}}
					/>
				);
			}

			default:
				return null;
		}
	};

	// Count active filters for badge (only visible filters)
	const activeFilterCount = useMemo(() => {
		return Object.entries(filterValues).filter(([id, value]) => visibleFilterIds.has(id) && isFilterValueActive(value)).length;
	}, [filterValues, visibleFilterIds]);

	return (
		<div className="datagridFilters">
			{!isCollapsed && (
				<div className="datagridFilters-row">
					{visibleFilters.map((filter) => (
						<div key={filter.id} className="datagridFilters-item">
							{renderFilter(filter)}
						</div>
					))}
				</div>
			)}
			<div className="datagridFilters-actions">
				{hasActiveFilters && !isCollapsed && (
					<button type="button" className="datagridFilters-btn datagridFilters-btn--clear" onClick={onClear}>
						Clear
					</button>
				)}
				<button type="button" className="datagridFilters-btn datagridFilters-btn--toggle" onClick={onToggleCollapse}>
					{isCollapsed ? (
						<>
							Expand filters
							{activeFilterCount > 0 && <span className="datagridFilters-badge">{activeFilterCount}</span>}
						</>
					) : (
						"Collapse filters"
					)}
				</button>
			</div>
		</div>
	);
}

export default DatagridFilters;
